// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the browse panel's search, so people type what they mean and
 * not the catalogue's keywords (owner, 2026-10-03).
 *
 * Instant and local: every dataset's title, keywords, categories,
 * kind, theme, package, source and description are split into words
 * once ({@link buildSmartIndex}); a query is matched word by word,
 * where a word can match exactly, as the start of a word ("hurric"),
 * with a typo ("temprature"), or through a synonym ("wildfire" →
 * fire, "sst" → sea surface temperature, a few Spanish words). What
 * kind of data a row is reads as words too, so "live fires" or
 * "future sea level" work with no filter chip.
 *
 * Ranking, as a person reads a list (owner, 2026-10-11): first the
 * titles that start with what was typed ("marine" → "Marine
 * Heatwaves", "Marine Vessel Traffic"), A to Z; then the titles that
 * have those words anywhere ("Observed Ocean Acidification (Copernicus
 * Marine)"), A to Z; then everything else — keywords, topics,
 * synonyms — rows matching more of the query's words first, then by
 * where the words matched (title over keywords over description).
 * Past the titles, only rows matching as many words as the best row
 * are returned, so "fires smoke" means both when some row has both,
 * and degrades to either when none does.
 *
 * Above all of that, our own rows come before the free SOS library's
 * (owner, 2026-10-24): every Pachamama Studios dataset is listed above
 * every SOS one, and the order above holds inside each of the two.
 *
 * The meaning-based half (`semanticSearch.ts`) adds rows this misses.
 * Pure: no DOM, no network.
 */

import type { Dataset } from '../types'
import { KIND_PREFIX, dataKindOf, displayTitle, packageOf, themesOf, type DataKind, type Theme } from './datasetTaxonomy'

/** Words that say nothing about which dataset is meant. */
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'of', 'in', 'on', 'at', 'for', 'to', 'and', 'or', 'with', 'by', 'from', 'about',
  'show', 'me', 'find', 'see', 'want', 'look', 'looking', 'i', 'is', 'are', 'what', 'where', 'how',
  'data', 'dataset', 'datasets', 'map', 'maps', 'some', 'any', 'all', 'please',
  'el', 'la', 'los', 'las', 'de', 'del', 'en', 'y', 'o', 'un', 'una', 'que', 'por', 'para', 'con',
])

/**
 * Synonym groups: any word or phrase in a group finds the others. A
 * synonym match counts a little less than the word itself, so a title
 * with the typed word still comes first.
 */
const SYNONYM_GROUPS: readonly (readonly string[])[] = [
  ['fire', 'wildfire', 'burn', 'burning', 'blaze', 'firms', 'fuego', 'incendio', 'incendios'],
  ['smoke', 'aerosol', 'aerosols', 'humo'],
  ['rain', 'rainfall', 'precipitation', 'lluvia', 'precipitacion'],
  ['temperature', 'temp', 'heat', 'warming', 'temperatura', 'calor'],
  ['sst', 'sea surface temperature'],
  ['co2', 'carbon dioxide'],
  ['ch4', 'methane', 'metano'],
  ['no2', 'nitrogen dioxide'],
  ['so2', 'sulfur dioxide', 'sulphur dioxide'],
  ['o3', 'ozone', 'ozono'],
  ['pm25', 'particulate', 'particles'],
  ['pollution', 'air quality', 'smog', 'contaminacion', 'calidad del aire'],
  ['hurricane', 'cyclone', 'typhoon', 'storm', 'huracan', 'tormenta', 'ciclon'],
  ['earthquake', 'quake', 'seismic', 'terremoto', 'sismo'],
  ['ice', 'glacier', 'frozen', 'hielo', 'glaciar'],
  ['snow', 'nieve'],
  ['wind', 'winds', 'viento'],
  ['cloud', 'clouds', 'nube', 'nubes'],
  ['ocean', 'sea', 'marine', 'oceano', 'mar'],
  ['flood', 'flooding', 'inundacion'],
  ['drought', 'dry', 'sequia'],
  ['vegetation', 'ndvi', 'plants', 'greenness', 'vegetacion'],
  ['forest', 'forests', 'trees', 'bosque'],
  ['population', 'people', 'poblacion'],
  ['night lights', 'city lights', 'black marble'],
  ['moon', 'lunar', 'luna'],
  ['sun', 'solar', 'sol'],
  ['arctic', 'polar', 'artico'],
  ['antarctic', 'antarctica', 'antartida'],
  ['earth', 'planet', 'tierra'],
  ['current', 'currents', 'corriente'],
  ['wave', 'waves', 'ola', 'olas'],
  ['coral', 'reef', 'arrecife'],
  ['volcano', 'volcanic', 'volcan'],
]

/** What kind of data a row is, in the words people use for it. */
const KIND_WORDS: Readonly<Record<DataKind, string>> = {
  'real-time': 'live real time realtime now current today latest tiempo real',
  forecast: 'forecast prediction predicted tomorrow outlook pronostico prevision',
  projection: 'projection projected future scenario scenarios ssp cmip6 2050 2100 climate change futuro proyeccion',
  reanalysis: 'reanalysis era5 past observed',
  historical: 'historical history past record records observed historico',
  catalogue: 'science on a sphere sos catalogue catalog',
}
const THEME_WORDS: Readonly<Record<Theme, string>> = {
  atmosphere: 'atmosphere air weather sky atmosfera clima',
  ocean: 'ocean sea water marine hydrosphere agua',
  climate: 'climate change',
  'fire-aq': 'fire smoke air quality pollution',
  land: 'land vegetation biosphere soil terrain',
  space: 'space planet astronomy universe espacio',
  people: 'people population human society',
}

const FIELD_WEIGHT = { title: 6, keywords: 4, facets: 3, source: 1.5, description: 1 } as const
type FieldName = keyof typeof FIELD_WEIGHT
const FIELDS = Object.keys(FIELD_WEIGHT) as FieldName[]

/** How much a kind of match counts, against an exact match of the typed word. */
const MATCH = { exact: 1, prefix: 0.7, fuzzy: 0.55, synonym: 0.85 } as const

/** The longest description read per row, in characters. */
const DESCRIPTION_CHARS = 1200
/** Most vocabulary words one typed prefix may stand for. */
const MAX_PREFIX_EXPANSIONS = 200
/**
 * A row scoring under this share of the best row is left out: a word
 * only in passing in a long description is not what was asked for.
 */
const MIN_SHARE_OF_BEST = 0.3

interface FieldIndex {
  tokens: string[]
  set: Set<string>
}

/** A title's words, every one, for the title-first tiers. */
interface TitleWords {
  /** As written, normalized ("heatwaves"), so a half-typed word finds its start. */
  raw: string[]
  /** Stemmed ("heatwave"), so "heatwave" finds "Heatwaves". */
  stemmed: string[]
}

interface IndexedDoc {
  id: string
  fields: Record<FieldName, FieldIndex>
  /** The title, normalized, for the whole-query bonus. */
  title: string
  /** The title as the card shows it, the kind the badge shows left off ("Real Time: Wind" → "Wind"). */
  shown: TitleWords
  /** The stored title, kind and all, so "forecast" still finds "Forecast: …" first. */
  full: TitleWords
  /** What the A-to-Z order of the title tiers reads. */
  sortKey: string
  /** A row of the free SOS library (`LIB_…` ids), listed below ours. */
  sos: boolean
}

export interface SmartIndex {
  docs: IndexedDoc[]
  /** Every distinct word in the index, for prefix and typo matching. */
  vocab: string[]
  vocabSet: ReadonlySet<string>
}

export interface SmartHit {
  id: string
  score: number
  /** Query words this row matched. */
  matched: number
  /** 0: the title starts with the query; 1: the title has its words; 2: found elsewhere. */
  tier: 0 | 1 | 2
}

/** Lower case, accents off, punctuation to spaces; "PM2.5" → "pm25". */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/(\d)\.(\d)/g, '$1$2')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** A light English stemmer: plural and -ing/-ed endings, never below four letters. */
export function stem(word: string): string {
  if (word.length <= 4 || /\d/.test(word)) return word
  if (word.endsWith('ies') && word.length > 5) return `${word.slice(0, -3)}y`
  if (word.endsWith('sses')) return word.slice(0, -2)
  if (word.endsWith('ing') && word.length > 6) return word.slice(0, -3)
  if (word.endsWith('ed') && word.length > 5) return word.slice(0, -2)
  if (word.endsWith('es') && /(ch|sh|x|ss)es$/.test(word)) return word.slice(0, -2)
  if (word.endsWith('s') && !word.endsWith('ss') && !word.endsWith('us') && !word.endsWith('is')) return word.slice(0, -1)
  return word
}

function words(text: string): string[] {
  const n = normalize(text)
  return n ? n.split(' ').map(stem) : []
}

function titleWords(text: string): TitleWords {
  const raw = normalize(text).split(' ').filter(Boolean)
  return { raw, stemmed: raw.map(stem) }
}

function field(text: string): FieldIndex {
  const tokens = words(text)
  return { tokens, set: new Set(tokens) }
}

/** Synonym lookup: a normalized word or phrase → the stemmed phrases it also means. */
const SYNONYMS: ReadonlyMap<string, readonly string[][]> = (() => {
  const map = new Map<string, string[][]>()
  for (const group of SYNONYM_GROUPS) {
    const phrases = group.map((p) => words(p))
    for (const phrase of phrases) {
      const key = phrase.join(' ')
      const others = phrases.filter((p) => p.join(' ') !== key)
      map.set(key, [...(map.get(key) ?? []), ...others])
    }
  }
  return map
})()

function formatWords(format: string): string {
  if (format.startsWith('video')) return 'video animation'
  if (format.startsWith('image')) return 'image'
  return format === 'khipu/story' ? 'story' : ''
}

/** Index a catalogue once; re-index only when the catalogue changes. */
export function buildSmartIndex(datasets: readonly Dataset[]): SmartIndex {
  const vocab = new Set<string>()
  const docs = datasets.map((d): IndexedDoc => {
    const enriched = d.enriched
    const facets = [
      KIND_WORDS[dataKindOf(d)],
      ...themesOf(d).map((t) => THEME_WORDS[t]),
      packageOf(d)?.replace(/^xr-/, '').replace(/-/g, ' ') ?? '',
      Object.keys(enriched?.categories ?? {}).join(' '),
      formatWords(d.format ?? ''),
    ].join(' ')
    const description = (enriched?.description ?? d.abstractTxt ?? '').slice(0, DESCRIPTION_CHARS)
    const shownTitle = displayTitle(d)
    const doc: IndexedDoc = {
      id: d.id,
      title: normalize(d.title),
      shown: titleWords(shownTitle),
      full: titleWords(d.title),
      sortKey: shownTitle,
      sos: d.id.startsWith('LIB_'),
      fields: {
        title: field(d.title),
        keywords: field([...(enriched?.keywords ?? []), ...(d.tags ?? [])].join(' ')),
        facets: field(facets),
        source: field([d.organization ?? '', enriched?.datasetDeveloper?.name ?? '', enriched?.visDeveloper?.name ?? ''].join(' ')),
        description: field(description),
      },
    }
    for (const name of FIELDS) for (const token of doc.fields[name].tokens) vocab.add(token)
    return doc
  })
  return { docs, vocab: [...vocab], vocabSet: vocab }
}

/** Edit distance with transpositions, giving up past `max`. */
export function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1
  const rows = a.length + 1
  const cols = b.length + 1
  let prevPrev = new Array<number>(cols).fill(0)
  let prev = Array.from({ length: cols }, (_, j) => j)
  for (let i = 1; i < rows; i++) {
    const cur = new Array<number>(cols)
    cur[0] = i
    let rowMin = cur[0]
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prevPrev[j - 2] + 1)
      cur[j] = v
      if (v < rowMin) rowMin = v
    }
    if (rowMin > max) return max + 1
    prevPrev = prev
    prev = cur
  }
  return prev[cols - 1]
}

/** One way a query word can be found: a phrase (one word or more) and what finding it is worth. */
interface Alternative {
  phrase: string[]
  factor: number
}

interface QueryWord {
  /** Single vocabulary words this query word stands for, with their worth. */
  words: Map<string, number>
  /** Phrases of two words or more ("sea surface temperature"). */
  phrases: Alternative[]
}

function addWord(map: Map<string, number>, word: string, factor: number): void {
  if ((map.get(word) ?? 0) < factor) map.set(word, factor)
}

/** Everything a single typed word may match in the index. */
function expandWord(word: string, index: SmartIndex, isLast: boolean): QueryWord {
  const alternatives: Alternative[] = [{ phrase: [word], factor: 1 }]
  for (const phrase of SYNONYMS.get(word) ?? []) alternatives.push({ phrase, factor: MATCH.synonym })
  const out: QueryWord = { words: new Map(), phrases: [] }
  for (const alt of alternatives) {
    if (alt.phrase.length > 1) {
      out.phrases.push(alt)
      continue
    }
    const w = alt.phrase[0]
    addWord(out.words, w, alt.factor)
    // A prefix finds longer words: the last word always (it is being
    // typed), any other from three letters ("temp" → temperature).
    const prefixMin = isLast ? 2 : 3
    // Typos only for a word the catalogue does not have: "fire" must not
    // also find "five" and "fine", but "temprature" should find the word.
    const fuzzyMax = index.vocabSet.has(w) || /\d/.test(w) ? 0 : w.length >= 8 ? 2 : w.length >= 5 ? 1 : 0
    let prefixes = 0
    for (const v of index.vocab) {
      if (v === w) continue
      if (w.length >= prefixMin && v.startsWith(w) && prefixes < MAX_PREFIX_EXPANSIONS) {
        prefixes++
        addWord(out.words, v, alt.factor * MATCH.prefix)
      } else if (fuzzyMax > 0 && editDistance(w, v, fuzzyMax) <= fuzzyMax) {
        addWord(out.words, v, alt.factor * MATCH.fuzzy)
      }
    }
  }
  return out
}

function containsPhrase(tokens: readonly string[], phrase: readonly string[]): boolean {
  outer: for (let i = 0; i + phrase.length <= tokens.length; i++) {
    for (let j = 0; j < phrase.length; j++) if (tokens[i + j] !== phrase[j]) continue outer
    return true
  }
  return false
}

/** The query's words, stop words out (unless that leaves nothing), multi-word synonyms joined. */
export function queryWords(query: string): string[] {
  const all = words(query)
  const kept = all.filter((w) => !STOP_WORDS.has(w))
  const base = kept.length > 0 ? kept : all
  // Join adjacent words that together form a known phrase ("air quality",
  // "sea surface temperature"), so the phrase is one query word.
  const out: string[] = []
  for (let i = 0; i < base.length; i++) {
    let joined = false
    for (const len of [3, 2]) {
      const phrase = base.slice(i, i + len).join(' ')
      if (base.length >= i + len && SYNONYMS.has(phrase)) {
        out.push(phrase)
        i += len - 1
        joined = true
        break
      }
    }
    if (!joined) out.push(base[i])
  }
  return out
}

/** A typed word, as written and stemmed; the last one may be half typed. */
interface TypedWord {
  raw: string
  stemmed: string
  /** A word that says nothing on its own ("the", "of", "show"). */
  stop: boolean
}

/** The query's words for the title tiers, as typed: nothing dropped, nothing joined. */
function typedWords(query: string): TypedWord[] {
  return normalize(query).split(' ')
    .filter(Boolean)
    .map((raw) => ({ raw, stemmed: stem(raw), stop: STOP_WORDS.has(raw) }))
}

/** Whether a title word is the typed word; the last one may be the start of it ("mari" → "marine"). */
function wordIs(title: TitleWords, i: number, typed: TypedWord, last: boolean): boolean {
  if (title.stemmed[i] === typed.stemmed) return true
  return last && (title.raw[i].startsWith(typed.raw) || title.stemmed[i].startsWith(typed.stemmed))
}

/**
 * Whether the title's first words are the typed ones, in order, every
 * word counting as a person reads it: "earth" does not start "From
 * Earthquake to Tsunami".
 */
function titleStartsWith(title: TitleWords, typed: readonly TypedWord[]): boolean {
  if (typed.length > title.raw.length) return false
  return typed.every((w, i) => wordIs(title, i, w, i === typed.length - 1))
}

/**
 * Whether the title has every typed word that means something, anywhere
 * ("show me marine" finds "Marine Heatwaves" here). A half-typed last
 * word counts from two letters, so one letter does not find every title.
 */
function titleHas(title: TitleWords, typed: readonly TypedWord[]): boolean {
  if (typed.every((w) => w.stop)) return false
  return typed.every((w, k) => {
    if (w.stop) return true
    const last = k === typed.length - 1 && w.raw.length >= 2
    return title.raw.some((_, i) => wordIs(title, i, w, last))
  })
}

const byTitle = new Intl.Collator('en', { sensitivity: 'base', numeric: true })

/**
 * Where a title stands for a query, for the smaller lists that filter
 * by plain words (Layers' real-time streams, lessons): 0
 * when it starts with the query, 1 when it has the query's words
 * anywhere, 2 otherwise. A kind written first ("Forecast: …") may be
 * skipped, as in the catalogue.
 */
export function titleTier(title: string, query: string): 0 | 1 | 2 {
  const typed = typedWords(query)
  if (typed.length === 0) return 2
  const full = titleWords(title)
  if (titleStartsWith(full, typed) || titleStartsWith(titleWords(title.replace(KIND_PREFIX, '')), typed)) return 0
  return titleHas(full, typed) ? 1 : 2
}

/** Two titles A to Z, as the title tiers order them (case and accents aside, "SSP2" before "SSP10"). */
export function compareTitles(a: string, b: string): number {
  return byTitle.compare(a, b)
}

/**
 * A filtered list in title-first order: titles starting with the query
 * A to Z, then titles with its words A to Z, then the rest as given.
 */
export function rankByTitle<T>(items: readonly T[], query: string, title: (item: T) => string): T[] {
  return items
    .map((item, i) => ({ item, i, tier: titleTier(title(item), query) }))
    .sort((a, b) => a.tier - b.tier || (a.tier < 2 ? compareTitles(title(a.item), title(b.item)) : 0) || a.i - b.i)
    .map((r) => r.item)
}

/**
 * Rank the index against a query. Returns null for an empty query
 * (nothing to rank), else the matching rows, best first.
 */
export function smartSearch(index: SmartIndex, query: string): SmartHit[] | null {
  const qWords = queryWords(query)
  if (qWords.length === 0) return null
  const expanded = qWords.map((w, i) => {
    if (w.includes(' ')) {
      // A known phrase: itself, and its synonyms.
      const phrase = w.split(' ')
      const q: QueryWord = { words: new Map(), phrases: [{ phrase, factor: 1 }] }
      for (const alt of SYNONYMS.get(w) ?? []) {
        if (alt.length === 1) addWord(q.words, alt[0], MATCH.synonym)
        else q.phrases.push({ phrase: alt, factor: MATCH.synonym })
      }
      return q
    }
    return expandWord(w, index, i === qWords.length - 1)
  })
  const wholeQuery = normalize(query)
  const typed = typedWords(query)
  const tierOf = (doc: IndexedDoc): 0 | 1 | 2 => {
    if (typed.length === 0) return 2
    if (titleStartsWith(doc.full, typed) || titleStartsWith(doc.shown, typed)) return 0
    return titleHas(doc.full, typed) ? 1 : 2
  }
  const sortKey = new Map(index.docs.map((d) => [d.id, d.sortKey]))
  const sos = new Map(index.docs.map((d) => [d.id, d.sos]))

  const hits: SmartHit[] = []
  for (const doc of index.docs) {
    const tier = tierOf(doc)
    let score = 0
    let matched = 0
    for (const q of expanded) {
      let best = 0
      for (const name of FIELDS) {
        const f = doc.fields[name]
        const weight = FIELD_WEIGHT[name]
        for (const [word, factor] of q.words) {
          if (weight * factor > best && f.set.has(word)) best = weight * factor
        }
        for (const alt of q.phrases) {
          const value = weight * alt.factor * alt.phrase.length
          if (value > best && containsPhrase(f.tokens, alt.phrase)) best = value
        }
      }
      if (best > 0) {
        matched++
        score += best
      }
    }
    if (matched === 0 && tier === 2) continue
    if (wholeQuery.length >= 3 && doc.title.includes(wholeQuery)) score += FIELD_WEIGHT.title
    hits.push({ id: doc.id, score, matched, tier })
  }
  // Rows matching every word the best rows match are kept; a row
  // matching fewer is kept only when it scores at least as well as the
  // best full match ("arctic" in a title beats "arctic" and "melting"
  // somewhere in a description), and ranks a little lower for it.
  const most = hits.reduce((m, h) => Math.max(m, h.matched), 0)
  const bestFull = hits.reduce((m, h) => (h.matched === most ? Math.max(m, h.score) : m), 0)
  const ranked = hits
    .filter((h) => h.tier < 2 || h.matched === most || h.score >= bestFull)
    .map((h) => ({ ...h, score: most > 0 ? h.score * (0.6 + (0.4 * h.matched) / most) : h.score }))
  const best = ranked.reduce((m, h) => Math.max(m, h.score), 0)
  // Our rows before the SOS library's; then titles first, A to Z within
  // each tier; the rest by how well they match.
  return ranked
    .filter((h) => h.tier < 2 || h.score >= best * MIN_SHARE_OF_BEST)
    .sort((a, b) => Number(sos.get(a.id)) - Number(sos.get(b.id))
      || a.tier - b.tier
      || (a.tier < 2 ? compareTitles(sortKey.get(a.id)!, sortKey.get(b.id)!) : b.score - a.score))
}
